/**
 * Reports of problems the app ran into (a page that crashed, an error nothing
 * caught), which it sends to its own server: there is no outside crash
 * reporting service. They go to the server's log, never to the database,
 * and carry nothing that says who someone is (see app/src/lib/errorReport.ts).
 */

export type ClientErrorSource = 'window' | 'promise' | 'render' | 'call';

export interface ClientErrorReport {
  message: string;
  stack?: string;
  where: ClientErrorSource;
  /** The page, with codes and keys taken out: "/m/[code]". */
  route: string;
  platform: 'web' | 'ios' | 'android';
  /** The app's version, from app/package.json. */
  version: string;
  /** The native app's build number. */
  build?: string;
}

/** The longest a report's parts may be. */
export const CLIENT_ERROR_LIMITS = { message: 500, stack: 4000, route: 100, version: 20, build: 20 } as const;

/** At most this many reports from one app session: a page stuck in a loop of errors does not flood the log. */
export const CLIENT_ERRORS_PER_SESSION = 10;

/**
 * Takes out of a report's words whatever could say who someone is, or open
 * something: email addresses, meeting codes, the keys in links (/r/…,
 * ?host=…), sign-in tokens, and IDs. The app does this before sending a
 * report, and the server again before writing one to its log.
 */
export function scrubReportText(text: string): string {
  return text
    .replace(/[^\s@<>"'`(),;:]+@[^\s@<>"'`(),;:]+\.[a-z]{2,}/gi, '[email]')
    .replace(/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, '[token]')
    .replace(/\bv\d+\.[\w-]+\.[\w-]+\.[\w-]+/g, '[token]')
    .replace(/([?&](?:host|token|code|state|ticket|session_id|resume|verifier|app_challenge)=)[^&#\s"'`)]+/gi, '$1[redacted]')
    .replace(/(\/r\/|\/api\/shared\/)[^/?#\s"'`)]+/g, '$1[token]')
    .replace(/(\/m\/)[^/?#\s"'`)]+/g, '$1[code]')
    .replace(/\b[a-z]{3}-[a-z]{4}-[a-z]{3}\b/gi, '[code]')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '[id]');
}
